import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight, CalendarCheck, MessageCircle, Phone, Navigation, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { useSettings } from "@/context/SettingsContext";
import { useTrackView } from "@/lib/useTrackView";
import { whatsappLink, telLink, mapsLink } from "@/lib/contact";
import { localBusiness, breadcrumbSchema } from "@/lib/schema";
import FinalCta from "@/sections/FinalCta";

export default function DistrictHub({ data: h }) {
  const settings = useSettings();
  useTrackView("hub", h.slug);

  const waMsg = `Merhaba, ${h.district} bölgesinde araç bakım hizmetleriniz hakkında bilgi almak istiyorum.`;
  const crumbs = [{ label: "Ana Sayfa", to: "/" }, { label: h.district }];
  const mapsEmbed = `https://www.google.com/maps?q=${encodeURIComponent(`Meguiar's İzmit ${h.district} Kocaeli`)}&output=embed`;
  const schema = [localBusiness(settings), breadcrumbSchema(crumbs)];

  return (
    <>
      <Seo title={h.seo_title} description={h.seo_description} image={h.hero_image} canonicalPath={`/${h.slug}`} jsonLd={schema} />
      <PageHero overline={`${h.district} • Kocaeli`} title={h.h1} subtitle={h.intro} image={h.hero_image} breadcrumbs={crumbs}>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/randevu" className="inline-flex items-center gap-2 bg-gold px-6 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300" data-testid="hub-randevu-btn">
            <CalendarCheck className="h-[18px] w-[18px]" /> Ücretsiz Randevu Al
          </Link>
          <a href={whatsappLink(settings, waMsg)} target="_blank" rel="noopener noreferrer" data-testid="hub-whatsapp-btn" className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.04] px-6 py-3.5 font-oswald text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:border-gold/50">
            <MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp'tan Yaz
          </a>
        </div>
      </PageHero>

      {/* Services in this district */}
      <section className="obsidian py-20 sm:py-28">
        <div className="container-x">
          <SectionHeading number="01" overline={`${h.district} Hizmetleri`} title={`${h.district}'te sunduğumuz hizmetler`} subtitle={`${h.district} ve çevresinde aracınız için tüm koruma ve bakım çözümleri.`} />
          {h.service_pages?.length > 0 ? (
            <div className="mt-12 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {h.service_pages.map((sp, i) => (
                <Reveal key={sp.slug} delay={(i % 3) * 0.06}>
                  <Link to={`/${sp.slug}`} data-testid={`hub-service-${sp.slug}`} className="group flex h-full flex-col overflow-hidden border border-white/10 bg-neutral-900 transition-colors hover:border-gold/40">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={`${sp.hero_image}?w=640`} alt={sp.h1} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[0.2] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                      <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/30 to-transparent" />
                    </div>
                    <div className="flex flex-1 items-center justify-between gap-2 p-5">
                      <h3 className="font-display text-lg font-medium text-white transition-colors group-hover:text-gold">{sp.service_name} {h.district}</h3>
                      <ArrowUpRight className="h-5 w-5 shrink-0 text-gold" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="mt-8 text-neutral-400">{h.why_us}</p>
          )}
        </div>
      </section>

      {/* Applications in this district */}
      {h.applications?.length > 0 && (
        <section className="border-y border-white/10 bg-black py-20 sm:py-28">
          <div className="container-x">
            <SectionHeading number="02" overline="Uygulamalar" title={`${h.district}'te yaptığımız uygulamalar`} subtitle={`${h.district} bölgesinden araç sahiplerinin bize emanet ettiği gerçek çalışmalar.`} />
            <div className="mt-12 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {h.applications.map((a, i) => (
                <Reveal key={a.slug} delay={(i % 3) * 0.07}>
                  <Link to={`/uygulamalar/${a.slug}`} data-testid={`hub-application-${a.slug}`} className="group block h-full overflow-hidden border border-white/10 bg-neutral-900 transition-colors hover:border-gold/40">
                    <div className="relative aspect-[16/11] overflow-hidden">
                      <img src={`${a.cover_image || a.after_image}?w=640`} alt={a.title} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[0.2] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                      <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/30 to-transparent" />
                      <span className="absolute left-4 top-4 border border-gold/40 bg-black/70 px-3 py-1 font-oswald text-[11px] font-bold uppercase tracking-[0.15em] text-gold backdrop-blur">{a.brand} {a.model}</span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-lg font-medium text-white transition-colors group-hover:text-gold">{a.title}</h3>
                      {a.duration && <span className="mt-2 inline-flex items-center gap-1.5 text-xs text-neutral-500"><Clock className="h-3.5 w-3.5 text-gold" /> {a.duration}</span>}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Map + nearby */}
      <section className="obsidian py-20 sm:py-28">
        <div className="container-x grid items-stretch gap-10 lg:grid-cols-2">
          <Reveal>
            <SectionHeading number="03" overline="Lokasyon" title={`${h.district}'ten kolay ulaşım`} subtitle={`${h.district} ve çevresine randevulu hizmet veriyoruz. Aracınızı getirin, en doğru bakım planını birlikte belirleyelim.`} />
            <div className="mt-8 space-y-2.5">
              <a href={telLink(settings)} className="flex items-center gap-4 border border-white/10 bg-white/[0.02] p-5 transition-colors hover:border-gold/40"><span className="flex h-11 w-11 items-center justify-center border border-gold/30 bg-gold/10 text-gold"><Phone className="h-5 w-5" /></span><span className="font-semibold text-white">{settings?.phone}</span></a>
              <a href={mapsLink(settings)} target="_blank" rel="noopener noreferrer" data-testid="hub-directions" className="flex items-center justify-center gap-2 bg-gold px-6 py-4 font-oswald text-sm font-bold uppercase tracking-[0.12em] text-black transition-colors hover:bg-gold-300"><Navigation className="h-4 w-4" /> Yol Tarifi Al</a>
            </div>
            {h.nearby_hubs?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-oswald text-xs uppercase tracking-[0.2em] text-neutral-500">Yakın ilçeler</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {h.nearby_hubs.map((n) => (<Link key={n.slug} to={`/${n.slug}`} data-testid={`hub-nearby-${n.slug}`} className="inline-flex items-center gap-1.5 border border-white/10 px-3 py-2 text-sm text-neutral-300 transition-colors hover:border-gold/40 hover:text-gold"><MapPin className="h-3.5 w-3.5 text-gold/70" /> {n.district}</Link>))}
                </div>
              </div>
            )}
          </Reveal>
          <Reveal delay={0.1} className="h-full">
            <div className="h-full min-h-[360px] overflow-hidden border border-white/10">
              <iframe title={`${h.district} konum`} src={mapsEmbed} className="h-full min-h-[360px] w-full grayscale-[0.4] contrast-[1.05]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </Reveal>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
